import { CURRENCY } from '@/app/constants';
import type { SupportedLanguage } from '@/app/types';

export const formatPriceDiscount = (discount: number, locale: SupportedLanguage) => {
  return locale === 'zh' ? (100 - discount) / 10 : discount;
};

export const getPriceDiscount = (price: number, originalPrice: number) => {
  return originalPrice > price ? 100 - Math.round((price / originalPrice) * 100) : 0;
};

/**
 * 计算一组商品的最大折扣百分比（营销文案「最高X折」用）。
 * 规则：
 * - 单品折扣：getPriceDiscount(price, original_price)，price 为券后价，故已含券折扣
 * - 年付综合折扣：年付券后价 vs 同 plan 月付原价×12，体现「年付优惠 + 券」叠加
 * 取全部候选的最大值。
 */
export const getMaxProductDiscount = (
  products: Array<{ price?: number; original_price?: number; plan_type?: string; billing_period?: string }>,
): number => {
  // 同 plan 月付原价，作为年付综合折扣的分母基准
  const monthlyOriginalByPlan = new Map<string, number>();
  for (const p of products) {
    if (p.billing_period === 'monthly' && p.plan_type) {
      monthlyOriginalByPlan.set(p.plan_type, p.original_price ?? p.price ?? 0);
    }
  }

  let max = 0;
  for (const p of products) {
    const price = p.price ?? 0;
    // 单品折扣（含券）
    max = Math.max(max, getPriceDiscount(price, p.original_price ?? 0));
    // 年付综合折扣（年付优惠 + 券）
    if (p.billing_period === 'yearly' && p.plan_type) {
      const monthlyBaseline = (monthlyOriginalByPlan.get(p.plan_type) ?? 0) * 12;
      if (monthlyBaseline > 0 && price < monthlyBaseline) {
        max = Math.max(max, 100 - Math.round((price / monthlyBaseline) * 100));
      }
    }
  }
  return max;
};

/**
 * 价格金额格式化（输入为「分」，输出纯数字字符串，不带货币符号）。
 * 规则：无小数→整数；有小数→保留两位并去末尾零。
 *   135800 → "1358"   2110 → "21.1"   2101 → "21.01"   1 → "0.01"   0 → "0"
 * 用于货币符号需单独排版（如大字号金额旁的小字号符号）的场景。
 */
export const formatAmount = (cents: number): string => {
  return (Math.round(cents) / 100).toFixed(2).replace(/\.?0+$/, '');
};

/**
 * 价格格式化（输入为「分」，输出带货币符号）。
 *   135800 → "¥1358"   2110 → "¥21.1"   1 → "¥0.01"   0 → "¥0"
 */
export const formatPrice = (cents: number): string => `${CURRENCY}${formatAmount(cents)}`;

/**
 * 价格金额格式化（输入为「分」，输出千分位分组的数字字符串，不带货币符号）。
 * 规则：整数部分按千分位分组；小数保留两位并去末尾零。
 *   864000 → "8,640"   4286000 → "42,860"   2110 → "21.1"   0 → "0"
 */
export const formatAmountGrouped = (cents: number): string => {
  const [intPart, decimalPart] = (Math.round(cents) / 100).toFixed(2).split('.');
  const groupedInt = Number(intPart).toLocaleString('en-US');
  const trimmedDecimal = decimalPart.replace(/0+$/, '');
  return trimmedDecimal ? `${groupedInt}.${trimmedDecimal}` : groupedInt;
};

/**
 * 价格格式化（输入为「分」，输出带货币符号 + 千分位分组）。
 *   864000 → "¥8,640"   4286000 → "¥42,860"   199900 → "¥1,999"   0 → "¥0"
 */
export const formatPriceGrouped = (cents: number): string => `${CURRENCY}${formatAmountGrouped(cents)}`;
